import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import { useTheme } from '@/context/ThemeContext';
import { useWiki } from '@/context/WikiContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';
import WikiHero from './components/WikiHero';
import WikiArticleView from './components/WikiArticleView';
import WikiComparisonTable from './components/WikiComparisonTable';
import BatteryQuiz from './components/BatteryQuiz';

export default function TechWikiPage() {
  const { theme } = useTheme();
  const light = theme === 'light';
  const { settings } = useWiki();
  const { settings: badgeSettings } = useBadgeVisibility();
  const published = settings.articles.filter((a) => a.published);
  const [activeId, setActiveId] = useState(published[0]?.id ?? '');
  const [quizOpen, setQuizOpen] = useState(false);

  const activeArticle = published.find((a) => a.id === activeId);

  const bg = light ? 'bg-gray-50' : 'bg-[#0D1B2A]';
  const border = light ? 'border-gray-100' : 'border-white/5';
  const textSecondary = light ? 'text-gray-400' : 'text-white/30';

  return (
    <div className={`min-h-screen ${bg} transition-colors duration-300`}>
      <SeoHead
        title="Battery Technology Guide | LiFePO4, AGM & Gel Explained | Stingray"
        description="Learn the differences between LiFePO4 lithium, AGM, and Gel marine batteries. Stingray's technology guide explains specs, pros, cons, and best use cases."
        keywords="LiFePO4 marine battery, AGM battery technology, gel battery, marine battery guide, lithium vs AGM"
        canonical="https://stingray-marine.com/tech-wiki"
      />
      <Navbar />

      <WikiHero settings={settings} activeId={activeId} onSelect={setActiveId} />

      <main className="w-full px-6 md:px-10 pb-24">
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <nav className={`flex items-center gap-2 text-xs mb-8 ${textSecondary}`}>
            <Link to="/" className="hover:text-[var(--accent)] transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className={light ? 'text-gray-600' : 'text-white/60'}>Tech Wiki</span>
            {activeArticle && (
              <>
                <i className="ri-arrow-right-s-line"></i>
                <span className={light ? 'text-gray-600' : 'text-white/60'}>{activeArticle.title}</span>
              </>
            )}
          </nav>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar nav */}
            <aside className="lg:w-56 shrink-0">
              <div className={`sticky top-24 rounded-2xl p-3 ${light ? 'bg-white border border-gray-100' : 'bg-[#1C2B3A] border border-white/5'}`}>
                <p className={`text-xs font-bold uppercase tracking-widest px-3 py-2 ${textSecondary}`}>Technologies</p>
                {published.map((article) => (
                  <button
                    key={article.id}
                    onClick={() => { setActiveId(article.id); setQuizOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer text-left mb-1 ${
                      activeId === article.id && !quizOpen
                        ? 'text-white'
                        : light ? 'text-gray-600 hover:bg-gray-50' : 'text-white/50 hover:bg-white/5'
                    }`}
                    style={activeId === article.id && !quizOpen ? { background: `${article.badgeColor}20`, color: article.badgeColor } : {}}
                  >
                    <div
                      className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                      style={activeId === article.id && !quizOpen ? { background: article.badgeColor } : { background: `${article.badgeColor}20` }}
                    >
                      <i className={`${article.icon} text-sm`} style={activeId === article.id && !quizOpen ? { color: 'white' } : { color: article.badgeColor }}></i>
                    </div>
                    <div>
                      <p className="font-bold text-xs">{article.title}</p>
                      {badgeSettings.wikiBadges && (
                        <p className={`text-xs ${textSecondary}`}>{article.badge}</p>
                      )}
                    </div>
                  </button>
                ))}

                <div className={`mt-3 pt-3 border-t ${border}`}>
                  <button
                    onClick={() => { setActiveId('compare'); setQuizOpen(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer text-left mb-1 ${
                      activeId === 'compare' && !quizOpen
                        ? 'text-white'
                        : light ? 'text-gray-600 hover:bg-gray-50' : 'text-white/50 hover:bg-white/5'
                    }`}
                    style={activeId === 'compare' && !quizOpen ? { background: 'color-mix(in srgb, var(--accent) 20%, transparent)', color: 'var(--accent)' } : {}}
                  >
                    <div
                      className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                      style={activeId === 'compare' && !quizOpen ? { background: 'var(--accent)' } : { background: 'color-mix(in srgb, var(--accent) 15%, transparent)' }}
                    >
                      <i className="ri-table-line text-sm" style={activeId === 'compare' && !quizOpen ? { color: 'white' } : { color: 'var(--accent)' }}></i>
                    </div>
                    <div>
                      <p className="font-bold text-xs">Compare All</p>
                      <p className={`text-xs ${textSecondary}`}>Side by side</p>
                    </div>
                  </button>
                </div>

                {/* Quiz button */}
                <div className={`mt-2 pt-2 border-t ${border}`}>
                  <button
                    onClick={() => setQuizOpen(true)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer text-left ${
                      quizOpen
                        ? 'text-white'
                        : light ? 'text-gray-600 hover:bg-gray-50' : 'text-white/50 hover:bg-white/5'
                    }`}
                    style={quizOpen ? { background: 'color-mix(in srgb, var(--accent) 20%, transparent)', color: 'var(--accent)' } : {}}
                  >
                    <div
                      className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                      style={quizOpen ? { background: 'var(--accent)' } : { background: 'color-mix(in srgb, var(--accent) 15%, transparent)' }}
                    >
                      <i className="ri-questionnaire-line text-sm" style={quizOpen ? { color: 'white' } : { color: 'var(--accent)' }}></i>
                    </div>
                    <div>
                      <p className="font-bold text-xs">Which Battery for Me?</p>
                      <p className={`text-xs ${textSecondary}`}>Find out with a quiz</p>
                    </div>
                    {!quizOpen && (
                      <span
                        className="ml-auto text-[9px] font-black px-1.5 py-0.5 rounded-full text-white whitespace-nowrap"
                        style={{ background: 'var(--accent)' }}
                      >
                        NEW
                      </span>
                    )}
                  </button>
                </div>

                {/* CTA */}
                <div className="mt-4 pt-4 border-t border-white/5">
                  <Link
                    to="/products"
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-xs font-bold text-white transition-all cursor-pointer whitespace-nowrap"
                    style={{ background: 'var(--accent)' }}
                  >
                    <i className="ri-compass-3-line"></i>
                    Explore Product Catalog
                  </Link>
                </div>
              </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              {quizOpen ? (
                <BatteryQuiz
                  articles={published}
                  onSelectArticle={(id) => { setActiveId(id); setQuizOpen(false); }}
                  onClose={() => setQuizOpen(false)}
                />
              ) : activeId === 'compare' ? (
                <WikiComparisonTable articles={published} onSelect={setActiveId} />
              ) : activeArticle ? (
                <WikiArticleView article={activeArticle} />
              ) : (
                <div className="text-center py-20">
                  <p className={light ? 'text-gray-400' : 'text-white/30'}>Select a technology to learn more.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
